/**
 * useSafetyAlerts — subscribes to the authenticated user's own
 * moderation_log inserts (RLS: moderation_log_self_read) and surfaces
 * any non-info entry as a toast in real time.
 *
 * This is the member-visible side of the behavioral risk pipeline:
 *   client heuristic → log-behavioral-signal edge → moderation_log row
 *   → realtime postgres_changes → toast nudge appears in the UI.
 *
 * Mount once at app root via <SafetyAlertsProvider />.
 */
import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth/AuthProvider";
import { toast } from "sonner";

interface ModerationLogRow {
  id: string;
  member_id: string;
  surface: string;
  message_id: string | null;
  severity: "info" | "warn" | "crisis" | "block";
  action: string | null;
  rationale: string | null;
  action_taken_at: string | null;
  created_at: string;
}

const stripPrefix = (rationale: string | null): string => {
  if (!rationale) return "Heads up — RAELA flagged this.";
  // Remove leading classifier tags like "[behavioral_risk:financial_request] "
  return rationale.replace(/^\[[^\]]+\]\s*/, "");
};

export const useSafetyAlerts = () => {
  const { user } = useAuth();
  const seen = useRef<Set<string>>(new Set());
  // Suppress toasts for log rows that already existed when we subscribed.
  const subscribedAt = useRef<number>(Date.now());

  useEffect(() => {
    if (!user) return;
    subscribedAt.current = Date.now();

    const channel = supabase
      .channel(`safety_alerts:${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "moderation_log",
          filter: `member_id=eq.${user.id}`,
        },
        (payload) => {
          const row = payload.new as ModerationLogRow;
          if (!row || seen.current.has(row.id)) return;
          seen.current.add(row.id);

          // Skip info-level unless it's clearly a behavioral nudge.
          const isBehavioral = (row.rationale ?? "").startsWith("[behavioral_risk:");
          if (row.severity === "info" && !isBehavioral) return;

          const message = stripPrefix(row.rationale);
          if (row.severity === "crisis" || row.severity === "block") {
            toast.error(message, { duration: 12_000 });
          } else if (row.severity === "warn") {
            toast.warning(message, { duration: 9_000 });
          } else {
            // info-level behavioral nudge
            toast(message, { duration: 7_000 });
          }
        },
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [user]);
};

/** Mount once at app root to enable safety alert toasts globally. */
export const SafetyAlertsProvider = ({ children }: { children: React.ReactNode }) => {
  useSafetyAlerts();
  return <>{children}</>;
};
